import type { ConfigState } from "./config.ts";
import { normalizeAgentId } from "../../../../src/routing/session-key.js";
import {
  cloneConfigObject,
  removePathValue,
  serializeConfigForm,
  setPathValue,
} from "./config/form-utils.ts";

const AGENT_ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/i;

export type AgentListEntryRecord = {
  id?: string;
  name?: string;
  workspace?: string;
  agentDir?: string;
  default?: boolean;
  subagents?: {
    allowAgents?: string[];
  };
  tools?: {
    profile?: string;
    allow?: string[];
    alsoAllow?: string[];
    deny?: string[];
  };
  skills?: string[];
  model?: unknown;
  identity?: {
    name?: string;
    emoji?: string;
    theme?: string;
    avatar?: string;
  };
  sandbox?: {
    mode?: "off" | "non-main" | "all";
    workspaceAccess?: "none" | "ro" | "rw";
    sessionToolsVisibility?: "spawned" | "all";
    scope?: "session" | "agent" | "shared";
    perSession?: boolean;
    workspaceRoot?: string;
  };
};

export type AgentConfigMutationResult =
  | { ok: true; agentId: string; index: number }
  | { ok: false; error: string };

export function isValidAgentId(raw: string): boolean {
  const trimmed = raw.trim();
  return trimmed.length > 0 && AGENT_ID_RE.test(trimmed);
}

export function getAgentsConfigList(
  config: Record<string, unknown> | null,
): AgentListEntryRecord[] {
  const list = (config as { agents?: { list?: unknown } })?.agents?.list;
  if (!Array.isArray(list)) {
    return [];
  }
  return list.filter((entry): entry is AgentListEntryRecord =>
    Boolean(entry && typeof entry === "object"),
  );
}

export function findAgentListIndex(
  config: Record<string, unknown> | null,
  agentId: string,
): number {
  const normalized = normalizeAgentId(agentId);
  const list = getAgentsConfigList(config);
  return list.findIndex((entry) => normalizeAgentId(entry.id) === normalized);
}

function commitConfigForm(state: ConfigState, base: Record<string, unknown>) {
  state.configForm = base;
  state.configFormDirty = true;
  if (state.configFormMode === "form") {
    state.configRaw = serializeConfigForm(base);
  }
}

function ensureAgentsListArray(base: Record<string, unknown>): AgentListEntryRecord[] {
  const agents = (base.agents ?? {}) as Record<string, unknown>;
  if (!base.agents || typeof base.agents !== "object") {
    base.agents = agents;
  }
  if (!Array.isArray(agents.list)) {
    agents.list = [];
  }
  return agents.list as AgentListEntryRecord[];
}

export function addAgentToConfigForm(
  state: ConfigState,
  params: { id: string; name?: string; workspace?: string; setDefault?: boolean },
): AgentConfigMutationResult {
  const rawId = params.id.trim();
  if (!isValidAgentId(rawId)) {
    return {
      ok: false,
      error: "Agent id must be 1–64 characters: letters, numbers, underscore, hyphen.",
    };
  }
  const id = normalizeAgentId(rawId);
  const base = cloneConfigObject(state.configForm ?? state.configSnapshot?.config ?? {});
  const list = ensureAgentsListArray(base);
  if (list.some((entry) => normalizeAgentId(entry.id) === id)) {
    return { ok: false, error: `Agent "${id}" already exists in agents.list.` };
  }
  const entry: AgentListEntryRecord = { id };
  const name = params.name?.trim();
  if (name) {
    entry.name = name;
  }
  const workspace = params.workspace?.trim();
  if (workspace) {
    entry.workspace = workspace;
  }
  if (params.setDefault) {
    for (const row of list) {
      if (row && typeof row === "object") {
        delete row.default;
      }
    }
    entry.default = true;
  }
  list.push(entry);
  commitConfigForm(state, base);
  return { ok: true, agentId: id, index: list.length - 1 };
}

export function ensureAgentInConfigForm(
  state: ConfigState,
  agentId: string,
): AgentConfigMutationResult {
  const id = normalizeAgentId(agentId);
  const base = cloneConfigObject(state.configForm ?? state.configSnapshot?.config ?? {});
  const existing = findAgentListIndex(base, id);
  if (existing >= 0) {
    return { ok: true, agentId: id, index: existing };
  }
  const list = ensureAgentsListArray(base);
  list.push({ id });
  commitConfigForm(state, base);
  return { ok: true, agentId: id, index: list.length - 1 };
}

export function removeAgentFromConfigForm(
  state: ConfigState,
  agentId: string,
): AgentConfigMutationResult {
  const id = normalizeAgentId(agentId);
  const base = cloneConfigObject(state.configForm ?? state.configSnapshot?.config ?? {});
  const list = ensureAgentsListArray(base);
  const index = list.findIndex((entry) => normalizeAgentId(entry.id) === id);
  if (index < 0) {
    return { ok: false, error: `Agent "${id}" is not in agents.list.` };
  }
  if (list.length <= 1) {
    return { ok: false, error: "Cannot remove the only agent in agents.list." };
  }
  const entry = list[index];
  if (entry?.default) {
    return { ok: false, error: "Unset instance default before deleting this agent." };
  }
  list.splice(index, 1);
  commitConfigForm(state, base);
  return { ok: true, agentId: id, index };
}

export type AgentFieldPatch = {
  name?: string;
  workspace?: string;
  agentDir?: string;
  identityName?: string;
  identityEmoji?: string;
  identityTheme?: string;
};

export type SandboxFieldPatch = {
  mode?: "off" | "non-main" | "all" | null;
  workspaceAccess?: "none" | "ro" | "rw" | null;
  sessionToolsVisibility?: "spawned" | "all" | null;
  scope?: "session" | "agent" | "shared" | null;
  workspaceRoot?: string | null;
};

function setOptionalString(
  path: Array<string | number>,
  value: string | undefined,
  base: Record<string, unknown>,
) {
  const trimmed = value?.trim() ?? "";
  if (trimmed) {
    setPathValue(base, path, trimmed);
  } else {
    removePathValue(base, path);
  }
}

export function getAgentAllowAgents(
  config: Record<string, unknown> | null,
  agentId: string,
): string[] | undefined {
  const index = findAgentListIndex(config, agentId);
  if (index < 0) {
    return undefined;
  }
  const list = getAgentsConfigList(config);
  const allow = list[index]?.subagents?.allowAgents;
  if (!Array.isArray(allow)) {
    return undefined;
  }
  return allow.map((id) => String(id).trim()).filter(Boolean);
}

export function setAgentAllowAgentsInConfigForm(
  state: ConfigState,
  agentId: string,
  allowAgents: string[] | undefined,
): AgentConfigMutationResult {
  const ensured = ensureAgentInConfigForm(state, agentId);
  if (!ensured.ok) {
    return ensured;
  }
  const base = cloneConfigObject(state.configForm ?? {});
  const index = findAgentListIndex(base, agentId);
  if (index < 0) {
    return { ok: false, error: "Agent not found after register." };
  }
  const prefix = ["agents", "list", index, "subagents"];
  if (!allowAgents || allowAgents.length === 0) {
    removePathValue(base, [...prefix, "allowAgents"]);
    const subagents = (base.agents as { list?: AgentListEntryRecord[] })?.list?.[index]?.subagents;
    if (subagents && typeof subagents === "object" && Object.keys(subagents).length === 0) {
      removePathValue(base, prefix);
    }
  } else {
    const normalized = [...new Set(allowAgents.map((id) => normalizeAgentId(id)))];
    setPathValue(base, [...prefix, "allowAgents"], normalized);
  }
  commitConfigForm(state, base);
  return { ok: true, agentId: normalizeAgentId(agentId), index };
}

export function updateAgentFieldsInConfigForm(
  state: ConfigState,
  agentId: string,
  patch: AgentFieldPatch,
): AgentConfigMutationResult {
  const ensured = ensureAgentInConfigForm(state, agentId);
  if (!ensured.ok) {
    return ensured;
  }
  const base = cloneConfigObject(state.configForm ?? {});
  const index = findAgentListIndex(base, agentId);
  if (index < 0) {
    return { ok: false, error: "Agent not found after register." };
  }
  const prefix = ["agents", "list", index];
  if (patch.name !== undefined) {
    setOptionalString([...prefix, "name"], patch.name, base);
  }
  if (patch.workspace !== undefined) {
    setOptionalString([...prefix, "workspace"], patch.workspace, base);
  }
  if (patch.agentDir !== undefined) {
    setOptionalString([...prefix, "agentDir"], patch.agentDir, base);
  }
  if (patch.identityName !== undefined) {
    setOptionalString([...prefix, "identity", "name"], patch.identityName, base);
  }
  if (patch.identityEmoji !== undefined) {
    setOptionalString([...prefix, "identity", "emoji"], patch.identityEmoji, base);
  }
  if (patch.identityTheme !== undefined) {
    setOptionalString([...prefix, "identity", "theme"], patch.identityTheme, base);
  }
  commitConfigForm(state, base);
  return { ok: true, agentId: normalizeAgentId(agentId), index };
}

export function updateSandboxFieldsInConfigForm(
  state: ConfigState,
  agentId: string,
  patch: SandboxFieldPatch,
): AgentConfigMutationResult {
  const ensured = ensureAgentInConfigForm(state, agentId);
  if (!ensured.ok) {
    return ensured;
  }
  const base = cloneConfigObject(state.configForm ?? {});
  const index = findAgentListIndex(base, agentId);
  if (index < 0) {
    return { ok: false, error: "Agent not found after register." };
  }
  const prefix = ["agents", "list", index, "sandbox"];

  if (
    patch.mode !== undefined ||
    patch.workspaceAccess !== undefined ||
    patch.sessionToolsVisibility !== undefined ||
    patch.scope !== undefined ||
    patch.workspaceRoot !== undefined
  ) {
    // Read existing sandbox so we merge instead of clobber
    const existing = (getSandboxValue(base, agentId) as Record<string, unknown>) ?? {};
    const merged: Record<string, unknown> = { ...existing };

    if (patch.mode !== undefined) {
      if (patch.mode === null) {
        delete merged["mode"];
      } else {
        merged["mode"] = patch.mode;
      }
    }
    if (patch.workspaceAccess !== undefined) {
      if (patch.workspaceAccess === null) {
        delete merged["workspaceAccess"];
      } else {
        merged["workspaceAccess"] = patch.workspaceAccess;
      }
    }
    if (patch.sessionToolsVisibility !== undefined) {
      if (patch.sessionToolsVisibility === null) {
        delete merged["sessionToolsVisibility"];
      } else {
        merged["sessionToolsVisibility"] = patch.sessionToolsVisibility;
      }
    }
    if (patch.scope !== undefined) {
      if (patch.scope === null) {
        delete merged["scope"];
      } else {
        merged["scope"] = patch.scope;
      }
    }
    if (patch.workspaceRoot !== undefined) {
      if (patch.workspaceRoot === null || patch.workspaceRoot.trim() === "") {
        delete merged["workspaceRoot"];
      } else {
        merged["workspaceRoot"] = patch.workspaceRoot.trim();
      }
    }

    if (Object.keys(merged).length === 0) {
      removePathValue(base, prefix);
    } else {
      setPathValue(base, prefix, merged);
    }
  }

  commitConfigForm(state, base);
  return { ok: true, agentId: normalizeAgentId(agentId), index };
}

function getSandboxValue(base: Record<string, unknown>, agentId: string): unknown {
  const list = getAgentsConfigList(base);
  const idx = list.findIndex((entry) => normalizeAgentId(entry.id) === normalizeAgentId(agentId));
  if (idx < 0) {
    return undefined;
  }
  return (list[idx] as Record<string, unknown>)?.sandbox;
}
